import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { HamButtonComponent } from '../../../../../aumico-components/src/lib';
import { SortDirection } from '../../../../../aumico-lib-cpmm-fe/src/lib/enums/sort-direction.enum';
import { TaskState } from '../../../../../aumico-lib-cpmm-fe/src/lib/enums/task-state.enum';
import { activeNavigation } from '../../enums/activeNavigation.enum';
import { ProjectStatus } from '../../enums/project-status.enum';
import { IssueDetailTableValue } from '../../interfaces/issue-detail-table-value.interface';
import { PersonProjectTableValue } from '../../interfaces/person-project-table-value.interface';

export type PersonProjectSortBy = 'personName' | 'periodEnd' | 'projectStatus';

/**
 * Column widths in px — the same as in the scss (group header row). The table is as wide as its
 * columns; wider than the page it scrolls sideways, the first column stays (like the company table).
 */
const COLUMN_WIDTH = { first: 300, period: 190, assignee: 180, status: 155, tag: 150, section: 220, action: 40 };

/**
 * REV-22155 — table of person projects (projects page "Personen" and the "Projekte" tab of a person).
 *
 * Looks like `aumico-project-overview-table` and uses its building blocks and styles, which stays
 * untouched. Pure view: data via inputs, every action via outputs — no store.
 * Columns: Person, Periode, Zuständig (only "Alle Projekte"), Status, Tag (only if the organization
 * has tags), then the progress per mandate management section.
 */
@Component({
    selector: 'aumico-person-project-overview-table',
    templateUrl: './person-project-overview-table.component.html',
    styleUrls: [
        '../project-overview-table.component.scss',
        './person-project-overview-table.component.scss',
    ],
})
export class PersonProjectOverviewTableComponent implements OnChanges {
    @Input() projects: PersonProjectTableValue[] = [];
    /** MY_PROJECTS hides "Zuständig"; COMPANY is used for the tab of one person. */
    @Input() activeNav: activeNavigation = activeNavigation.ALL_PROJECTS;
    /** The "Projekte" tab of one person: no "Person" column, the period comes first. */
    @Input() hidePersonColumn = false;
    @Input() showTags = false;
    @Input() anyActiveFilter = false;
    @Input() activeSortBy: PersonProjectSortBy | null = null;
    @Input() activeSortOrder: SortDirection | null = null;
    @Input() allProjectsSelected = false;
    @Input() selectedProjects: Set<string> = new Set();
    @Input() disableNewProjectButton = false;

    @Output() sortProjects = new EventEmitter<{ sortBy: PersonProjectSortBy | null; sortOrder: SortDirection | null }>();
    @Output() updateProjectStatus = new EventEmitter<{ projectId: string; status: ProjectStatus; updateTasks: boolean }>();
    @Output() deleteProject = new EventEmitter<string>();
    /** Row click → mandate management page; with `section` it scrolls there. */
    @Output() selectProject = new EventEmitter<{ project: PersonProjectTableValue; section?: string }>();
    /** Tick a task in the progress flyout. */
    @Output() changeTaskState = new EventEmitter<{ projectId: string; taskId: string; taskState: TaskState }>();
    @Output() toggleCheckAllProject = new EventEmitter<boolean>();
    @Output() toggleCheckProject = new EventEmitter<string>();
    @Output() addNewProject = new EventEmitter<void>();

    readonly navEnum = activeNavigation;
    readonly ProjectStatus = ProjectStatus;
    readonly TaskState = TaskState;

    summaries: string[] = [];
    columnClasses: string[] = [];
    totalTableLength = 0;

    openedActionFlyoutProjectId: string | null = null;
    actionFlyoutPosition: { top: number; left: number; right: number } = { top: 0, left: 0, right: 0 };
    openProjectDeletionConfirmationDialog = false;
    openAutoTaskUpdateDialog = false;
    projectIdToBeChanged: string | null = null;

    hoveredProjectId: string | null = null;
    hoveredSummary: string | null = null;
    issueDetailsFlyoutPosition: { top: number; left: number } = { top: 0, left: 0 };
    private flyoutCloseTimer: ReturnType<typeof setTimeout> | null = null;
    private lastSelectedProjectIndex = -1;

    get showAssigneeColumn(): boolean {
        return this.activeNav !== activeNavigation.MY_PROJECTS;
    }

    ngOnChanges(): void {
        this.summaries = Array.from(new Set(this.projects.flatMap((p) => (p.workflowData ?? []).map((w) => w.summary))));
        this.columnClasses = [
            'stick-0',
            ...(this.hidePersonColumn ? [] : ['middle-cols']),
            ...(this.showAssigneeColumn ? ['middle-cols'] : []),
            'middle-cols',
            ...(this.showTags ? ['middle-cols'] : []),
            ...this.summaries.map(() => 'middle-cols-MM'),
            'sticky-action',
        ];
        this.totalTableLength =
            COLUMN_WIDTH.first +
            (this.hidePersonColumn ? 0 : COLUMN_WIDTH.period) +
            (this.showAssigneeColumn ? COLUMN_WIDTH.assignee : 0) +
            COLUMN_WIDTH.status +
            (this.showTags ? COLUMN_WIDTH.tag : 0) +
            this.summaries.length * COLUMN_WIDTH.section +
            COLUMN_WIDTH.action;
        this.lastSelectedProjectIndex = -1;
    }

    getSummaryBadge(element: PersonProjectTableValue, summary: string): string {
        const entry = element.workflowData?.find((w) => w.summary === summary);
        if (entry == null) {
            return '-';
        }
        return entry.totalIssueCount === 0 ? 'N/A' : `${entry.completedIssueCount}/${entry.totalIssueCount}`;
    }

    /** Same colours as the company table: done, overdue, due within 7 days. */
    taskStatus(element: PersonProjectTableValue, summary: string): '' | 'done' | 'overdue' | 'almostOverdue' {
        const entry = element.workflowData?.find((w) => w.summary === summary);
        if (entry == null || entry.totalIssueCount === 0) {
            return '';
        }
        if (entry.completedIssueCount === entry.totalIssueCount) {
            return 'done';
        }
        const openDueDates = entry.issuesDetails
            .filter((i) => i.state !== TaskState.DONE && i.state !== TaskState.NOT_APPLICABLE && i.dueDate)
            .map((i) => new Date(i.dueDate).getTime());
        if (openDueDates.length === 0) {
            return '';
        }
        const days = (Math.min(...openDueDates) - Date.now()) / (1000 * 60 * 60 * 24);
        if (days < 0) {
            return 'overdue';
        }
        return days <= 7 ? 'almostOverdue' : '';
    }

    hoveredIssues(): IssueDetailTableValue[] {
        const project = this.projects.find((p) => p.projectId === this.hoveredProjectId);
        const entry = project?.workflowData?.find((w) => w.summary === this.hoveredSummary);
        return entry ? entry.issuesDetails.filter((issue) => issue.state > TaskState.NOT_APPLICABLE) : [];
    }

    isOverdue(dueDate: string | Date): boolean {
        return new Date(dueDate) < new Date();
    }

    isDueSoon(dueDate: string | Date): boolean {
        const days = (new Date(dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
        return days >= 0 && days <= 7;
    }

    startHover(event: MouseEvent, projectId: string, summary: string): void {
        this.keepFlyoutOpen();
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        this.issueDetailsFlyoutPosition = { top: rect.bottom, left: Math.max(10, rect.left - 100) };
        this.hoveredProjectId = projectId;
        this.hoveredSummary = summary;
    }

    endHover(): void {
        this.flyoutCloseTimer = setTimeout(() => {
            this.hoveredProjectId = null;
            this.hoveredSummary = null;
        }, 500);
    }

    keepFlyoutOpen(): void {
        if (this.flyoutCloseTimer != null) {
            clearTimeout(this.flyoutCloseTimer);
            this.flyoutCloseTimer = null;
        }
    }

    sortBy(sortBy: PersonProjectSortBy): void {
        if (this.activeSortBy !== sortBy) {
            this.sortProjects.emit({ sortBy, sortOrder: SortDirection.ASC });
        } else if (this.activeSortOrder === SortDirection.ASC) {
            this.sortProjects.emit({ sortBy, sortOrder: SortDirection.DESC });
        } else {
            this.sortProjects.emit({ sortBy: null, sortOrder: null });
        }
    }

    openActionFlyout(projectId: string, button: HamButtonComponent): void {
        const rect = button.nativeButton?.nativeElement?.getBoundingClientRect();
        if (!rect) {
            return;
        }
        const top = window.innerHeight - rect.bottom < 90 ? rect.bottom - 100 : rect.bottom;
        this.actionFlyoutPosition = { top, right: rect.right, left: rect.left - 200 };
        this.openedActionFlyoutProjectId = projectId;
    }

    closeActionFlyout(): void {
        this.openedActionFlyoutProjectId = null;
    }

    /** "Erledigt": with tasks, ask first whether the open tasks are closed too (like companies). */
    onMarkDone(element: PersonProjectTableValue): void {
        if (this.summaries.length > 0) {
            this.projectIdToBeChanged = element.projectId;
            this.openAutoTaskUpdateDialog = true;
        } else {
            this.onUpdateStatus(element.projectId, ProjectStatus.DONE, false);
        }
    }

    onUpdateStatus(projectId: string | null, status: ProjectStatus, updateTasks: boolean): void {
        this.openAutoTaskUpdateDialog = false;
        if (projectId != null) {
            this.updateProjectStatus.emit({ projectId, status, updateTasks });
        }
    }

    onDeleteProject(projectId: string): void {
        this.openProjectDeletionConfirmationDialog = false;
        this.deleteProject.emit(projectId);
    }

    onSelect(element: PersonProjectTableValue, section?: string): void {
        this.selectProject.emit(section != null ? { project: element, section } : { project: element });
    }

    onChangeTaskState(projectId: string, taskId: string, taskState: TaskState): void {
        this.changeTaskState.emit({ projectId, taskId, taskState });
    }

    onProjectCheckboxClick(projectId: string, event: MouseEvent): void {
        event.stopPropagation();
        const index = this.projects.findIndex((p) => p.projectId === projectId);
        if (event.shiftKey && this.lastSelectedProjectIndex !== -1 && index !== -1) {
            const target = !this.selectedProjects.has(projectId);
            const [from, to] = [Math.min(this.lastSelectedProjectIndex, index), Math.max(this.lastSelectedProjectIndex, index)];
            for (const project of this.projects.slice(from, to + 1)) {
                if (this.selectedProjects.has(project.projectId) !== target) {
                    this.toggleCheckProject.emit(project.projectId);
                }
            }
        } else {
            this.toggleCheckProject.emit(projectId);
        }
        this.lastSelectedProjectIndex = index;
    }

    onToggleCheckAllProject(): void {
        this.toggleCheckAllProject.emit(!this.allProjectsSelected);
    }
}
